import {
  CURRENT_OWNER_LABELS,
  WAITING_FOR_LABELS,
  type AuthorizationCase,
} from '@/services/authorization-case.service';
import {
  WORK_QUEUE_STAGE_LABELS,
  type Finding,
  type Inspection,
} from '@/services/inspection.service';
import type { StepExecutionView } from '@/features/inspection/services/procedure-execution.service';
import { fmtDateTime } from '../utils';
import { CaseContextField, CaseContextFieldGrid } from './CaseContextField';

interface Props {
  kase: AuthorizationCase;
  inspection: Inspection | undefined;
  engineerRole: string | null;
  step: StepExecutionView | null;
  finding: Finding | null;
}

export function CaseSummaryCard({ kase, inspection, engineerRole, step, finding }: Props) {
  const requestType =
    kase.approvalRequestTypeName ??
    (kase.isCustom && kase.customApprovalName
      ? `${kase.customApprovalName} (Custom)`
      : 'Not classified');

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="border-b border-border px-5 py-3.5">
        <h3 className="text-sm font-semibold text-foreground">Authorization Request Summary</h3>
      </div>
      <div className="px-5 py-4">
        <CaseContextFieldGrid>
          <CaseContextField label="Authorization Number" value={kase.caseNumber} />
          <CaseContextField
            label="Inspection Number"
            value={inspection?.inspectionNumber ?? kase.inspectionId}
          />
          <CaseContextField
            label="Job Number"
            value={inspection?.intakeNumber ?? 'No intake linked'}
          />
          <CaseContextField label="Request Type" value={requestType} />
          <CaseContextField label="Requested By" value={kase.requestedByName} />
          <CaseContextField label="Engineer Role" value={engineerRole ?? 'Not recorded'} />
          <CaseContextField label="Requested Date & Time" value={fmtDateTime(kase.requestedAt)} />
          <CaseContextField
            label="Current Inspection Stage"
            value={inspection ? WORK_QUEUE_STAGE_LABELS[inspection.currentStage] : '—'}
          />
          {kase.procedureStepExecutionId && (
            <CaseContextField label="Procedure Step" value={step?.title ?? 'Loading…'} />
          )}
          {kase.findingId && (
            <CaseContextField
              label="Finding Reference"
              value={finding ? `#${finding.findingNumber} — ${finding.title}` : 'Loading…'}
            />
          )}
          <CaseContextField label="Current Owner" value={CURRENT_OWNER_LABELS[kase.currentOwner]} />
          <CaseContextField label="Waiting For" value={WAITING_FOR_LABELS[kase.waitingFor]} />
          <CaseContextField label="Priority" value={kase.priorityName ?? 'Not set'} />
        </CaseContextFieldGrid>
      </div>
    </div>
  );
}
